import { Popover, Stack } from "@mui/material";
import { useState } from "react";
import { useLanguage } from "../../../../../stores/LanguageStore";
import {
  useLeadsContainerStore,
  useLeadsSheetStore,
} from "../../../../../stores/LeadsStore";
import { markReply, updateProspect } from "../../../../../api/PRMAPI";
import { IconThumbUp } from "@tabler/icons-react";
import { ActionIcon, Button, Tooltip } from "@mantine/core";
import { IconThumbUpFilled } from "@tabler/icons-react";
import { IconThumbDownFilled } from "@tabler/icons-react";

const PositiveChanger = ({
  containerId,
  prospectId,
  itemId,
  is_positive,
  isInDetail,
  isAnswer,
  reply_type,
  actualProspect,
  setActualProspect,
  container,
  inTable,
  ...props
}) => {
  const lang = useLanguage((s) => s.language);
  const currentIndex = useLeadsSheetStore((s) => s.index);
  const current = useLeadsSheetStore((s) => s.current);
  const setCurrent = useLeadsSheetStore((s) => s.setCurrent);
  const containers = useLeadsContainerStore((s) => s.containers);
  const updateContainerItem = useLeadsContainerStore(
    (s) => s.updateContainerItem
  );
  const updateContainer = useLeadsContainerStore((s) => s.updateContainer);

  const [anchor, setAnchor] = useState(null);
  const [opened, setOpened] = useState(false);

  const handleOpen = (e) => {
    e.stopPropagation();
    setOpened(true);
    setAnchor(e.target);
  };
  const handleClose = () => {
    setOpened(false);
  };

  const [loading, setLoading] = useState(false);
  const handlePositive = (newValue) => {
    setLoading(
      newValue === null
        ? "neutral"
        : newValue === true
        ? "positive"
        : "negative"
    );

    const main = isAnswer ? markReply : updateProspect;
    main({
      id: itemId,
      is_positive: newValue,
      contact_id: prospectId,
      reply_id: itemId,
      history_type_reply: reply_type,
    })
      .then(() => {
        handleClose();

        setTimeout(() => {
          if (isInDetail) {
            if (currentIndex !== null) {
              updateContainerItem(containerId, itemId, {
                is_positive: newValue,
              });
            }
            setCurrent({ ...current, is_positive: newValue });
          }
          if (isAnswer) {
            let newProspect = { ...actualProspect };
            let i = newProspect.history.findIndex((x) => x.id === itemId);
            newProspect.history[i] = {
              ...newProspect.history[i],
              is_positive_reply: newValue,
            };
            updateContainerItem(containerId, itemId, {
              history: newProspect.history,
            });

            // Update prospect
            let newPositive;
            if (current?.is_positive === null) {
              newPositive = newValue;
            }
            if (current?.is_positive === false && newValue === true) {
              newPositive = true;
            }
            if (current?.is_positive === true && newValue === false) {
              newPositive = true;
            }
            setCurrent({
              ...newProspect,
              is_positive: newPositive,
            });
            setActualProspect({
              ...newProspect,
              is_positive: newPositive,
            });

            let newContainer = containers.find((x) => {
              if (newValue === true) {
                return x?.type_default_status === "answerer_positive";
              }
              x?.type_default_status === "answerer_negative";
            });
            if (!current?.custom_status && newValue !== null && newContainer) {
              // Delete the item from its container
              let newItemsOnCurrentContainer = container.items.filter(
                (item) => item.id !== current.id
              );
              updateContainer(container.id, {
                items: newItemsOnCurrentContainer,
                total: container.total - 1,
                needCacheReset: true,
              });

              // Add the item to its next container
              let newItemsFuture = [...newContainer.items];
              let newItem = { ...current, custom_status: newContainer.id };
              newItemsFuture.unshift({
                ...newItem,
                status_changed_date: new Date(),
              });
              updateContainer(newContainer.id, {
                total: newContainer.total + 1,
                items: newItemsFuture,
                needCacheReset: true,
              });
            }
          } else {
            updateContainerItem(containerId, itemId, { is_positive: newValue });
          }
        }, 150);
      })
      .finally(() => setLoading(false));
  };

  return (
    <>
      <Tooltip
        label={
          is_positive === null
            ? lang.prm.neutral
            : is_positive === true
            ? lang.prm.positive
            : lang.prm.negative
        }
      >
        <ActionIcon
          size={isInDetail ? "lg" : isAnswer ? "md" : "xs"}
          variant={isInDetail ? "light" : "subtle"}
          color={
            is_positive === null ? "gray" : is_positive ? "green.8" : "red"
          }
          onClick={handleOpen}
          mt={isInDetail ? 0 : isAnswer ? 0 : -4}
          pos={inTable ? "static" : "absolute"}
          top={8}
          right={8}
          {...props}
        >
          {is_positive === null ? (
            <IconThumbUp size={isInDetail ? 20 : isAnswer ? 18 : 14} />
          ) : is_positive === false ? (
            <IconThumbDownFilled size={isInDetail ? 20 : isAnswer ? 18 : 14} />
          ) : (
            <IconThumbUpFilled size={isInDetail ? 20 : isAnswer ? 18 : 14} />
          )}
        </ActionIcon>
      </Tooltip>

      {opened && (
        <Popover
          open={opened}
          anchorEl={anchor}
          onClose={handleClose}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
          transformOrigin={{ vertical: "top", horizontal: "center" }}
          sx={{ mt: 0.25 }}
        >
          <Stack p={0.5} spacing={0.5}>
            {!is_positive && (
              <Button
                onClick={(e) => {
                  e.stopPropagation();
                  handlePositive(true);
                }}
                color="green.8"
                size="xs"
                variant="light"
                leftSection={<IconThumbUpFilled size={16} />}
                loading={loading === "positive"}
                justify="start"
              >
                {lang.prm.positive}
              </Button>
            )}
            {(is_positive || is_positive === null) && (
              <Button
                onClick={(e) => {
                  e.stopPropagation();
                  handlePositive(false);
                }}
                color="red"
                size="xs"
                variant="light"
                leftSection={<IconThumbDownFilled size={16} />}
                loading={loading === "negative"}
                justify="start"
              >
                {lang.prm.negative}
              </Button>
            )}
            {is_positive !== null && (
              <Button
                onClick={(e) => {
                  e.stopPropagation();
                  handlePositive(null);
                }}
                color="gray"
                size="xs"
                variant="light"
                loading={loading === "neutral"}
                leftSection={<IconThumbUp size={16} />}
                justify="start"
              >
                {lang.prm.neutral}
              </Button>
            )}
          </Stack>
        </Popover>
      )}
    </>
  );
};

export default PositiveChanger;
